import type { CaptureResult, CapturedNetworkRequest } from 'posthog-js';

import { scrubSecrets } from './sentry-scrub';

const REDACTED = '[redacted]';

/** Blanks sign-in keys in a URL or screen name before PostHog sees it. */
export function scrubPosthogText(text: string): string {
  return scrubSecrets(text, REDACTED);
}

/**
 * `before_send` hook: login links (/login/<jwt>) and password-recovery links carry a key that signs
 * the customer in, and PostHog copies the page address into $current_url, $pathname, $referrer,
 * $screen_name and the $initial_* person properties of every event.
 */
export function scrubPosthogEvent(event: CaptureResult | null): CaptureResult | null {
  if (!event) {
    return event;
  }
  const scrubbed: CaptureResult = { ...event, properties: scrubStrings(event.properties) };
  if (event.$set) {
    scrubbed.$set = scrubStrings(event.$set);
  }
  if (event.$set_once) {
    scrubbed.$set_once = scrubStrings(event.$set_once);
  }
  return scrubbed;
}

/** Session replay passes the page address (and each recorded request) through this mask. */
export function scrubPosthogRecordedRequest(request: CapturedNetworkRequest): CapturedNetworkRequest {
  return request.name ? { ...request, name: scrubPosthogText(request.name) } : request;
}

// Arrays are left alone: replay snapshots are large and their page address is masked above.
function scrubStrings<T extends Record<string, any>>(values: T): T {
  return Object.fromEntries(
    Object.entries(values).map(([key, value]) => [key, scrubProperty(value)])
  ) as T;
}

function scrubProperty(value: unknown): unknown {
  if (typeof value === 'string') {
    return scrubPosthogText(value);
  }
  if (value && typeof value === 'object' && !Array.isArray(value)) {
    return scrubStrings(value as Record<string, unknown>);
  }
  return value;
}
